import React, { useState, useMemo, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { useParams } from "react-router-dom";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonStatusBadge from "Components/Common/CommonStatusBadge";
import { getPaginationRange } from "helpers/pagination";
import { useSessionProgressService } from "modules/Session/Session/api/useSession";
import { GetUserData, CurrentUser } from "common/service/CommonService";
import CommonSearch from "Components/Common/CommonSearch";
import TruncatedText from "Components/Common/TruncatedText";
import styles from "../css/sessionProgressModal.module.scss";

interface SessionProgressModalProps {
  isOpen: boolean;
  toggle: () => void;
  sessionData: any;
}

const SessionProgressModal: React.FC<SessionProgressModalProps> = ({
  isOpen,
  toggle,
  sessionData,
}) => {
  const { id } = useParams();
  const user = GetUserData() as CurrentUser;
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  type SessionFilter = "ALL" | "PENDING" | "IN_PROGRESS" | "COMPLETED" | "SKIPPED";

  const [filter, setFilter] = useState<SessionFilter>("ALL");

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobileOrTablet = windowWidth <= 768;

  const { SessionProgressQuery } = useSessionProgressService({
    sessionid: isOpen ? id || sessionData?.sessionid : null,
    searchtext: searchText,
    currentpage: currentPage,
    pagesize: 10,
    tabstatus: filter,
  });

  const onSearch = (text: string | null) => {
    setSearchText(text ?? "");
    setCurrentPage(1);
  };

  const progressData = SessionProgressQuery?.data?.data || [];
  const totalRecords = progressData[0]?.total_count || 0;

  // Determine how many aliquots to show based on the first item in the response
  const aliquotKeys = useMemo(() => {
    if (progressData.length > 0 && progressData[0].aliquots) {
      return Object.keys(progressData[0].aliquots).sort(
        (a, b) => Number(a) - Number(b),
      );
    }
    return [];
  }, [progressData]);

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return (
          <i
            className="ri-checkbox-circle-fill text-success fs-18"
            title="Completed"
          />
        );
      case "IN_PROGRESS":
        return (
          <i className="ri-loader-2-line text-info fs-18" title="In Progress" />
        );
      case "SKIPPED":
        return (
          <i
            className="ri-skip-forward-line text-warning fs-18"
            title="Skipped"
            style={{ color: "#b45309" }}
          />
        );
      case "PENDING":
      default:
        return (
          <i className="ri-time-line text-warning fs-18" title="Pending" />
        );
    }
  };

  const getStatusBadge = (val: string) => {
    let color = "secondary";
    let text = "Pending";

    const status = val?.toUpperCase();
    if (status === "COMPLETED") {
      color = "success";
      text = "Completed";
    } else if (status === "IN_PROGRESS") {
      color = "info";
      text = "In Progress";
    } else if (status === "SKIPPED") {
      color = "warning";
      text = "Skipped";
    }

    return (
      <div className="d-flex justify-content-center">
        <CommonStatusBadge
          isActive={true}
          activeText={text}
          activeColor={color}
          style={{ width: "95px" }}
        />
      </div>
    );
  };

  // Define columns for CommonTableIndexWise
  const columns = useMemo(() => {
    const baseColumns = [
      {
        key: "subjectno",
        label: "Subject No.",
        textCenter: true,
        render: (val: any) => <span className="fw-bold text-dark">{val}</span>,
      },
      {
        key: "subjectbarcode",
        label: "Subject Barcode",
        textCenter: true,
        render: (val: any) => <span className="text-muted fs-13">{val}</span>,
      },
      {
        key: "vaccutainer_status",
        label: "Vacutainer",
        textCenter: true,
        render: (val: any) => (
          <div className="d-flex justify-content-center">
            {getStatusIcon(val)}
          </div>
        ),
      },
    ];

    const aliquotCols = aliquotKeys.map((key) => ({
      key: `aliquot_${key}`,
      label: `Aliquot ${key}`,
      textCenter: true,
      render: (_: any, row: any) => (
        <div className="d-flex justify-content-center">
          {getStatusIcon(row.aliquots[key])}
        </div>
      ),
    }));

    const statusColumn = {
      key: "overall_status",
      label: "Overall Status",
      textCenter: true,
      render: (val: any) => getStatusBadge(val),
    };

    const skipReasonColumn = {
      key: "skipreason",
      label: "Skip Reason",
      textCenter: true,
      render: (val: any, row: any) => {
        const reason = val || row.skipreason;
        if (row.overall_status?.toLowerCase() === "skipped" && reason) {
          return (
            <div className={styles.tableTruncatedCell}>
              <TruncatedText text={reason.trim()} maxWidth="100%" />
            </div>
          );
        }
        return <span className="text-muted">—</span>;
      },
    };

    return [...baseColumns, ...aliquotCols, statusColumn, skipReasonColumn];
  }, [aliquotKeys]);

  const handleFilterChange = (newFilter: SessionFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      size="xl"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        <div className="d-flex align-items-center gap-2">
          <i className="ri-bar-chart-horizontal-line text-primary fs-20"></i>
          <span className="d-block">Session Progress Details</span>
          <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 ms-2">
            {sessionData?.sessionno || sessionData?.sessionid || "-"}
          </span>
        </div>
      </ModalHeader>

      <ModalBody className="py-2 px-2 px-md-4">
        {/* Header Info */}
        <div className={styles.summaryHeader}>
          <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "16px" }}>
            Subject Scan Summary
          </h6>
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            Monitor real-time progress of subjects and their respective samples.
          </p>
        </div>

        {/* Search and Filters */}
        <div className={styles.controlsRow}>
          <div className={styles.filterContainer}>
            <button
              className={`btn btn-sm rounded-pill px-3 ${filter === "ALL" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("ALL")}
            >
              All
            </button>
            <button
              className={`btn btn-sm rounded-pill px-3 ${filter === "COMPLETED" ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("COMPLETED")}
            >
              Completed
            </button>
            <button
              className={`btn btn-sm rounded-pill px-3 ${filter === "PENDING" ? "btn-warning text-dark" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("PENDING")}
            >
              Pending
            </button>
            <button
              className={`btn btn-sm rounded-pill px-3 ${filter === "IN_PROGRESS" ? "btn-info text-white" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("IN_PROGRESS")}
            >
              In Progress
            </button>
            <button
              className={`btn btn-sm rounded-pill px-3 ${filter === "SKIPPED" ? "btn-warning text-dark fw-bold" : "btn-outline-secondary"}`}
              onClick={() => handleFilterChange("SKIPPED")}
            >
              Skipped
            </button>
          </div>

          <div className={styles.searchWrapper}>
            <CommonSearch
              onSearch={onSearch}
              searchText={searchText}
              placeholder="Search Subject..."
            />
          </div>
        </div>

        {/* Render Mobile/Tablet Cards or Desktop Table */}
        {isMobileOrTablet ? (
          <>
            {SessionProgressQuery.isPending ? (
              <div className={styles.cardListContainer}>
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <div key={`skeleton-${i}`} className={styles.subjectCard} style={{ opacity: 0.6 }}>
                      <div className={styles.cardHeader}>
                        <div style={{ width: "80px", height: "18px", backgroundColor: "#e2e8f0", borderRadius: "4px" }} />
                        <div style={{ width: "95px", height: "24px", backgroundColor: "#e2e8f0", borderRadius: "12px" }} />
                      </div>
                      <div className={styles.cardBody} style={{ marginTop: "8px" }}>
                        <div style={{ width: "150px", height: "16px", backgroundColor: "#e2e8f0", borderRadius: "4px" }} />
                        <div className={styles.statusGrid}>
                          <div style={{ width: "100%", height: "28px", backgroundColor: "#e2e8f0", borderRadius: "6px" }} />
                          <div style={{ width: "100%", height: "28px", backgroundColor: "#e2e8f0", borderRadius: "6px" }} />
                          <div style={{ width: "100%", height: "28px", backgroundColor: "#e2e8f0", borderRadius: "6px" }} />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : progressData.length > 0 ? (
              <>
                <div className={styles.cardListContainer}>
                  {progressData.map((item: any, idx: number) => (
                    <div key={item.id || idx} className={styles.subjectCard}>
                      <div className={styles.cardHeader}>
                        <span className={styles.subjectNo}>Subject No. {item.subjectno}</span>
                        {getStatusBadge(item.overall_status)}
                      </div>
                      <div className={styles.cardBody}>
                        <div className={styles.barcodeRow}>
                          <i className="ri-barcode-line" />
                          <span>Barcode: {item.subjectbarcode || "-"}</span>
                        </div>
                        {item.overall_status?.toLowerCase() === "skipped" && item.skipreason && (
                          <div className={styles.skipReasonBanner}>
                            <i className="ri-information-fill text-warning me-1 fs-14" />
                            <span className={styles.skipReasonLabel}>Skip Reason:</span>
                            <span className={styles.skipReasonText}>
                              {item.skipreason.trim()}
                            </span>
                          </div>
                        )}
                        <div className={styles.statusGrid}>
                          <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>Vacutainer:</span>
                            <span className={styles.statusIcon}>{getStatusIcon(item.vaccutainer_status)}</span>
                          </div>
                          {aliquotKeys.map((key) => (
                            <div key={key} className={styles.statusItem}>
                              <span className={styles.statusLabel}>Aliquot {key}:</span>
                              <span className={styles.statusIcon}>
                                {getStatusIcon(item.aliquots?.[key])}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile Pagination */}
                {Math.ceil(totalRecords / 10) > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                    <small className="text-muted">
                      Showing {(currentPage - 1) * 10 + 1}–
                      {Math.min(currentPage * 10, totalRecords)} of {totalRecords}
                    </small>

                    <Pagination size="sm" className="mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          previous
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                      </PaginationItem>

                      {getPaginationRange(currentPage, Math.ceil(totalRecords / 10)).map((item, key) => (
                        <PaginationItem key={key} active={currentPage === item} disabled={item === "..."}>
                          <PaginationLink
                            onClick={item === "..." ? undefined : () => setCurrentPage(Number(item))}
                            style={item === "..." ? { cursor: 'default' } : undefined}
                          >
                            {item}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem disabled={currentPage === Math.ceil(totalRecords / 10)}>
                        <PaginationLink
                          next
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-muted my-4">No records found</div>
            )}
          </>
        ) : (
          <CommonTableIndexWise
            columns={columns}
            data={progressData}
            loading={SessionProgressQuery.isPending}
            pageSize={10}
            currentPage={currentPage}
            totalRecords={totalRecords}
            onPageChange={(page) => setCurrentPage(page)}
            actions={{}}
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default SessionProgressModal;
